/*
	`useFTMessageGrowl allows you to trigger growls in response to socket messages that trigger
	`onFTMessage` events.

	It takes one argument, `triggers`, which is expected to be an array of functions.
		- Each function takes the message `detail` as a parameter and returns the notification action
		payload, which will be used for the `addNotification` action creator and dispatched.
			- If it doesn't return a payload, nothing will be dispatched.

	The hook will register the triggers on mount, and remove them on unmount, so it should be called
	in components that are in scope for as long as you want to trigger growls.
		- Because of the asynchronous nature of socket messages, this means you'll mostly want to use
		it in components that are always rendered, like the app header, so that when you navigate from
		one route to another, the growls will still appear.

	Example usage:
	```
	function exportVersionsNotifications(detail){
		let { task, status, data } = detail?.message || {};
		if(task === "Export versions"){
			if(status === "Success"){
				return {
					type: "success",
					messageHeader: "Success",
					message: `${data?.request?.description || "Versions export"} is ready to download.`,
				}
			}
			if(status === "Failure"){
				return {
					type: "error",
					messageHeader: "Error Exporting Versions",
					message: data?.response?.error || "Something went wrong",
				};
			}
		}
	}

	function AppHeader(){
		useFTMessageGrowl([exportVersionsNotifications]);
		...
	}
	```
*/

import { addNotification } from "reducers/Notifications";
import { useDispatch } from "react-redux";
import useFTMessage from "hooks/Notification";

export default function useFTMessageGrowl(triggers = []){
	const dispatch = useDispatch();

	useFTMessage((e) => {
		let { detail } = e;
		triggers.forEach((trigger) => {
			let payload = trigger(detail);
			if(payload){
				dispatch(addNotification(payload));
			}
		});
	});
}
